import type { Editor } from "@tiptap/core";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { NodeSelection, TextSelection } from "@tiptap/pm/state";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { serialiseVariableToken, type IVariableToken } from "../variable-token";
import { VARIABLE_MENTION_PILL_MARGIN_Y } from "./variable-mention.constants";
import type {
    IVariableMentionOptions,
    IVariableMentionStorage,
    IVariableMentionSwapSession,
} from "./variable-mention.types";
import { getDefaultVariablePillProps, isEmptyVariable, variableTokenFromNode } from "./variable-mention.utilities";
import { VariablePill } from "./variable-pill";

/**
 * Each editor's pending refocus frame, shared by all its pills, so opening any pill's swap cancels a refocus that
 * another pill's swap scheduled when it closed.
 */
const pendingRefocusFrames = new WeakMap<Editor, number>();

/**
 * The caret just after a pill, and after the space that follows it if there is one: where the insert flow leaves it.
 * @param doc      The document holding the pill.
 * @param position The pill's position.
 * @returns A collapsed selection after the pill.
 */
const selectionAfterPill = (doc: ProseMirrorNode, position: number): TextSelection => {
    const after = position + (doc.nodeAt(position)?.nodeSize ?? 1);
    const nodeAfter = doc.resolve(after).nodeAfter;
    const followedBySpace = nodeAfter?.isText === true && nodeAfter.text?.startsWith(" ") === true;

    return TextSelection.create(doc, followedBySpace ? after + 1 : after);
};

/**
 * React node view for a `variableMention` node: a `VariablePill` and its tooltip in an inline `<span>`, so the
 * pill sits in the paragraph's text flow, with a small vertical margin so lines of pills don't touch. What the pill draws
 * comes from `getPillProps`; an empty variable's pill, the grey placeholder, gets the same margin, so a line holding one
 * is as tall as a line holding a filled pill.
 *
 * Nothing stops event propagation, so ProseMirror still sees the mousedown and turns a click on the pill into a
 * `NodeSelection`, which comes back here as `selected`.
 *
 * With the `renderSwap` option, the view also owns a swap session:
 * - The pill's click opens it, but only when the pill is the `NodeSelection`. ProseMirror sets that on mouseup, before
 *   the click. A keyboard selection never opens it, which keeps the two-step Backspace intact.
 * - `openVariableMentionSwap` opens it without a click: the command selects the pill and leaves a pending swap in the
 *   extension's storage, which the view takes once it renders selected at that position. Its `onCancel`, if any,
 *   replaces the refocus when `onClose` or a loss of selection ends the swap without a replace. Turning read-only
 *   ends it without either.
 * - While it is open, the pill renders inside `renderSwap`'s output, keyed by the session so every open mounts fresh.
 * - It ends on `onClose`, on the pill losing the selection, and on the editor turning read-only (`setEditable` emits
 *   `update`).
 * - `onReplace` resolves the pill's position at call time, since the document can change while the swap is open, and
 *   ends the swap once the replace succeeds.
 * - Applied or dismissed, the swap leaves the caret after the pill (and its space), not the pill selected, so the next
 *   key typed can't replace the pill. The editor refocuses a frame later, and opening another swap cancels that.
 * @param props.node The ProseMirror node, whose attributes hold the variable.
 * @param props.selected Whether the node is inside the current selection.
 * @param props.extension The extension, for the consumer's `getPillProps` and `renderSwap`.
 * @param props.editor The editor, for the selection, editability, focus and the replace command.
 * @param props.getPos The node's current position.
 * @returns {JSX.Element} The node view.
 */
export const VariableMentionView = ({ node, selected, extension, editor, getPos }: NodeViewProps) => {
    const variable = variableTokenFromNode(node);
    const isEmpty = isEmptyVariable(variable);
    // What ties a swap to the variable it opened on. An empty variable has no token, so its fields stand in as JSON:
    // `variableTokenFromNode` always builds them in the same key order, so equal variables give equal strings.
    const swapToken = isEmpty ? JSON.stringify(variable) : serialiseVariableToken(variable);
    // Tiptap types an extension's options as `any`; the annotation gives them their real shape.
    const { getPillProps = getDefaultVariablePillProps, renderSwap }: IVariableMentionOptions = extension.options;
    const storage: IVariableMentionStorage = extension.storage;
    const [swap, setSwapState] = useState<IVariableMentionSwapSession | null>(null);
    // The open session, readable from `onClose`, which can run before a re-render (right after a replace).
    const swapRef = useRef<IVariableMentionSwapSession | null>(null);
    const swapCountRef = useRef(0);
    const refocusFrameRef = useRef<number | null>(null);

    const setSwap = useCallback((session: IVariableMentionSwapSession | null) => {
        swapRef.current = session;
        setSwapState(session);
    }, []);

    // Losing the selection ends the swap, so re-selecting the pill by keyboard never reopens it. A swap with `onCancel`
    // hands it the end, as `onClose` does.
    useEffect(() => {
        if (!selected) {
            const session = swapRef.current;
            setSwap(null);
            session?.onCancel?.();
        }
    }, [selected, setSwap]);

    // Turning read-only (e.g. a response starts streaming) ends the swap without replacing anything.
    useEffect(() => {
        const closeWhenReadOnly = () => {
            if (!editor.isEditable) {
                setSwap(null);
            }
        };

        editor.on("update", closeWhenReadOnly);

        return () => {
            editor.off("update", closeWhenReadOnly);
        };
    }, [editor, setSwap]);

    // Cancel this pill's pending refocus when its node view goes, e.g. the pill is deleted.
    useEffect(
        () => () => {
            const frame = refocusFrameRef.current;

            if (frame !== null) {
                cancelAnimationFrame(frame);

                if (pendingRefocusFrames.get(editor) === frame) {
                    pendingRefocusFrames.delete(editor);
                }
            }
        },
        [editor]
    );

    /** Cancel the editor's pending refocus, whichever pill scheduled it. */
    const cancelRefocus = useCallback(() => {
        const frame = pendingRefocusFrames.get(editor);

        if (typeof frame === "number") {
            cancelAnimationFrame(frame);
            pendingRefocusFrames.delete(editor);
        }

        refocusFrameRef.current = null;
    }, [editor]);

    /**
     * Refocus the editor in a frame, first moving the caret after the pill if the pill is still the selection. The delay
     * is what makes the focus stick after a click outside, whose own focus change happens after the dropdown's
     * outside-click handler. The caret only moves off the very selection that was there when the swap closed, so a
     * click elsewhere in the text, or an undo that reselects the pill within the frame, keeps the selection it made.
     */
    const scheduleRefocus = useCallback(() => {
        cancelRefocus();

        const selectionAtClose = editor.state.selection;
        const frame = requestAnimationFrame(() => {
            refocusFrameRef.current = null;
            pendingRefocusFrames.delete(editor);

            if (editor.isDestroyed || !editor.isEditable) {
                return;
            }

            const position = getPos();
            const { selection } = editor.state;
            const isPillStillSelected =
                selection === selectionAtClose && selection instanceof NodeSelection && selection.from === position;

            if (typeof position === "number" && isPillStillSelected) {
                editor.view.dispatch(editor.state.tr.setSelection(selectionAfterPill(editor.state.doc, position)));
            }

            editor.view.focus();
        });

        refocusFrameRef.current = frame;
        pendingRefocusFrames.set(editor, frame);
    }, [cancelRefocus, editor, getPos]);

    /**
     * Open the swap, when the pill is the `NodeSelection` of an editable editor with `renderSwap`.
     * @param onCancel What ends the swap instead of the refocus when it closes without a replace.
     */
    const openSwap = (onCancel?: () => void) => {
        const position = getPos();
        const { selection } = editor.state;
        const isThisPillSelected =
            typeof position === "number" && selection instanceof NodeSelection && selection.from === position;

        if (typeof renderSwap !== "function" || !editor.isEditable || !isThisPillSelected) {
            return;
        }

        // A swap that closed in this same frame must not pull focus back to the editor from this one.
        cancelRefocus();
        swapCountRef.current += 1;
        setSwap({ id: swapCountRef.current, token: swapToken, onCancel });
    };

    // Take a swap `openVariableMentionSwap` left for this pill, once it renders selected (a pill inserted in the same
    // transaction mounts selected).
    useEffect(() => {
        const pending = storage.pendingSwap;

        if (selected && pending !== null && pending.position === getPos()) {
            storage.pendingSwap = null;
            openSwap(pending.onCancel);
        }
        // `openSwap` reads the latest render's values; only a change of selection can make a pending swap apply.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    const closeSwap = useCallback(() => {
        const session = swapRef.current;
        setSwap(null);

        // A swap opened with `onCancel` hands the document and focus to its caller. After a replace there is no
        // session left, so a replace's own `onClose` refocuses as usual.
        if (typeof session?.onCancel === "function") {
            session.onCancel();
            return;
        }

        if (!editor.isDestroyed && editor.isEditable) {
            scheduleRefocus();
        }
    }, [editor, scheduleRefocus, setSwap]);

    const replace = useCallback(
        (next: IVariableToken) => {
            // Resolved now, not when the swap opened: text typed or inserted since then moves the pill.
            const position = getPos();

            // A consumer may call back a microtask late, after the editor turned read-only.
            if (!editor.isDestroyed && editor.isEditable && typeof position === "number") {
                // The caret moves after the pill in the replace's own transaction, so the undo step restores the old
                // variable with the pill selected, as it was before the swap.
                const isReplaced = editor.commands.command(({ tr, commands }) => {
                    if (!commands.replaceVariableMention(position, next)) {
                        return false;
                    }

                    tr.setSelection(selectionAfterPill(tr.doc, position));

                    return true;
                });

                // End the session outright rather than relying on the token changing: an unchanged variable would
                // leave it open, and an undo (which restores the old token and the selection) would reopen it.
                if (isReplaced) {
                    setSwap(null);
                    scheduleRefocus();
                }
            }
        },
        [editor, getPos, scheduleRefocus, setSwap]
    );

    const pill = (
        <VariablePill
            {...getPillProps(variable)}
            selected={selected}
            onClick={() => openSwap()}
            my={VARIABLE_MENTION_PILL_MARGIN_Y}
        />
    );

    // A session only applies to the variable it opened on: a replace (or a reused node view) ends it. Written as one
    // inline condition so TypeScript narrows `renderSwap` and `swap` inside it.
    const swapElement =
        typeof renderSwap === "function" && selected && swap !== null && swap.token === swapToken ? (
            <Fragment key={swap.id}>
                {renderSwap({ token: variable, pill, onReplace: replace, onClose: closeSwap })}
            </Fragment>
        ) : null;

    return <NodeViewWrapper as="span">{swapElement ?? pill}</NodeViewWrapper>;
};
